<template>
    <q-card :class="$q.screen.gt.lg ? 'dialog__lg' : 'dialog__xs'">
        <!-- Dialog header -->
        <q-bar class="bg-primary text-white">
            {{ $t('Quality events') }}
            <q-space />
            <q-btn @click="close" class="text-white" icon="close" dense flat>
                <q-tooltip>{{ $t('Close') }}</q-tooltip>
            </q-btn>
        </q-bar>

        <div class="row q-mr-md q-ml-md q-mt-md">
            <div class="col-12 text-grey-9 text-h6">
                <div class="text-h6" v-if="filters.assetName">
                    {{ filters.assetName }}
                </div>
                <div class="text-h6" v-else>
                    {{ filters.productName }}
                </div>
                <div class="text-subtitle2">
                    {{ filters.defectName }}
                </div>
                <div class="text-grey-9 text-subtitle2">
                    {{ formatDate(filters.start) }} -
                    {{ formatDate(filters.end) }}
                </div>
            </div>
        </div>

        <!-- Quality Event Table -->
        <q-card-section>
            <q-table
                @request="throttledOnRequest"
                v-model:pagination="table.pagination"
                :rows="table.data"
                :columns="table.columns"
                :rows-per-page-options="[table.pagination.rowsPerPage]"
                :loading="table.isLoading"
                :no-data-label="$t('No downtime event available')"
                :grid="$q.screen.lt.lg"
                table-style="min-height: 30vh"
                row-key="id"
                binary-state-sort
                flat
                bordered
            >
                <!-- Status column -->
                <template v-slot:body-cell-status="props">
                    <q-td :props="props">
                        <div>
                            <q-chip
                                class="event-table-status"
                                style="font-weight: 500"
                                outline
                                color="darker-yellow"
                                :label="$t('Quality')"
                                square
                            >
                            </q-chip>
                        </div>
                    </q-td>
                </template>

                <!-- Defect column -->
                <template v-slot:body-cell-defect__name="props">
                    <q-td :props="props">
                        <div v-if="props.row.defect_name">
                            {{ props.row.defect_name }}
                        </div>
                        <div v-else>
                            <missing-defect-label
                                v-model="defectSelectorProps.selection"
                                :eventId="props.row.id"
                                :assetId="filters.assetId"
                                @refreshList="refreshList()"
                                @clearSelection="
                                    defectSelectorProps.selection = null
                                "
                                :baseURL="defectSelectorProps.url"
                                :queryParams="defectSelectorProps.queryParams"
                                :label="defectSelectorProps.label"
                                :dialogTitle="defectSelectorProps.dialogTitle"
                                :searchLabel="defectSelectorProps.searchLabel"
                            >
                                <template v-slot:item="data">
                                    <defect-card
                                        @select="
                                            select(
                                                data.props.row,
                                                defectSelectorProps
                                            )
                                        "
                                        :name="data.props.row.name"
                                        :code="data.props.row.code"
                                        :description="
                                            data.props.row.description
                                        "
                                        :isSelected="
                                            defectSelectorProps.selection &&
                                            defectSelectorProps.selection
                                                .value === data.props.row.id
                                        "
                                        :isSelectable="true"
                                    />
                                </template>
                            </missing-defect-label>
                        </div>
                    </q-td>
                </template>

                <!-- Card template for small devices -->
                <template v-slot:item="props">
                    <div class="full-width q-pa-xs">
                        <q-card class="full-width full-height">
                            <q-card-section>
                                <div class="row">
                                    <!-- Fields column (breaks down to 2 columns on medium devices, single column on small devices) -->
                                    <div class="col-12">
                                        <div class="row">
                                            <!-- First column of fields -->
                                            <div
                                                class="col-xs-12 col-md-6 full-height"
                                            >
                                                <q-chip
                                                    class="event-table-status q-mb-sm"
                                                    style="font-weight: 500"
                                                    outline
                                                    color="darker-yellow"
                                                    :label="$t('Quality')"
                                                    square
                                                >
                                                </q-chip>

                                                <!-- Date range field -->
                                                <div
                                                    class="row"
                                                    :style="
                                                        $q.screen.lt.sm
                                                            ? 'font-size: 11.5px;'
                                                            : ''
                                                    "
                                                >
                                                    <q-icon
                                                        name="far fa-calendar-alt"
                                                        class="q-mr-xs"
                                                        size="sm"
                                                        color="grey"
                                                    >
                                                        <q-tooltip
                                                            anchor="bottom middle"
                                                            self="top middle"
                                                        >
                                                            {{
                                                                $t('Date range')
                                                            }}
                                                        </q-tooltip>
                                                    </q-icon>
                                                    <div
                                                        v-if="
                                                            props.row.end ===
                                                            null
                                                        "
                                                        class="q-ml-xs"
                                                    >
                                                        {{
                                                            `${formatDate(
                                                                props.row.start
                                                            )} (${$t(
                                                                'Ongoing'
                                                            )})`
                                                        }}
                                                    </div>
                                                    <div
                                                        v-else
                                                        class="q-ml-xs"
                                                        style="margin-top: 2px"
                                                    >
                                                        {{
                                                            `${formatDate(
                                                                props.row.start
                                                            )} - ${formatDate(
                                                                props.row.end
                                                            )}`
                                                        }}
                                                    </div>
                                                </div>
                                                <div
                                                    v-if="props.row.defect"
                                                    class="row q-mt-xs"
                                                >
                                                    <q-icon
                                                        name="far fa-exclamation-circle"
                                                        class="q-mr-xs"
                                                        size="sm"
                                                        color="grey"
                                                    >
                                                        <q-tooltip
                                                            anchor="bottom middle"
                                                            self="top middle"
                                                        >
                                                            {{ $t('Defect') }}
                                                        </q-tooltip>
                                                    </q-icon>
                                                    {{ props.row.defect_name }}
                                                </div>
                                                <div class="q-mt-xs" v-else>
                                                    <missing-defect-label
                                                        v-model="
                                                            defectSelectorProps.selection
                                                        "
                                                        :eventId="props.row.id"
                                                        :assetId="
                                                            filters.assetId
                                                        "
                                                        @refreshList="
                                                            refreshList()
                                                        "
                                                        @clearSelection="
                                                            defectSelectorProps.selection =
                                                                null
                                                        "
                                                        :baseURL="
                                                            defectSelectorProps.url
                                                        "
                                                        :queryParams="
                                                            defectSelectorProps.queryParams
                                                        "
                                                        :label="
                                                            defectSelectorProps.label
                                                        "
                                                        :dialogTitle="
                                                            defectSelectorProps.dialogTitle
                                                        "
                                                        :searchLabel="
                                                            defectSelectorProps.searchLabel
                                                        "
                                                    >
                                                        <template
                                                            v-slot:item="data"
                                                        >
                                                            <defect-card
                                                                @select="
                                                                    select(
                                                                        data
                                                                            .props
                                                                            .row,
                                                                        defectSelectorProps
                                                                    )
                                                                "
                                                                :name="
                                                                    data.props
                                                                        .row
                                                                        .name
                                                                "
                                                                :code="
                                                                    data.props
                                                                        .row
                                                                        .code
                                                                "
                                                                :description="
                                                                    data.props
                                                                        .row
                                                                        .description
                                                                "
                                                                :isSelected="
                                                                    defectSelectorProps.selection &&
                                                                    defectSelectorProps
                                                                        .selection
                                                                        .value ===
                                                                        data
                                                                            .props
                                                                            .row
                                                                            .id
                                                                "
                                                                :isSelectable="
                                                                    true
                                                                "
                                                            />
                                                        </template>
                                                    </missing-defect-label>
                                                </div>
                                            </div>

                                            <!-- Second column of fields -->
                                            <div
                                                class="col-xs-12 col-md-6 full-height"
                                            >
                                                <div
                                                    v-if="props.row.asset"
                                                    class="q-mt-xs"
                                                >
                                                    <q-icon
                                                        name="far fa-industry-alt"
                                                        class="q-mr-xs"
                                                        size="sm"
                                                        color="grey"
                                                    >
                                                        <q-tooltip
                                                            anchor="bottom middle"
                                                            self="top middle"
                                                        >
                                                            {{ $t('Asset') }}
                                                        </q-tooltip>
                                                    </q-icon>
                                                    {{ props.row.asset_name }}
                                                </div>
                                                <div
                                                    v-if="props.row.product"
                                                    class="q-mt-xs"
                                                >
                                                    <q-icon
                                                        name="far fa-box"
                                                        class="q-mr-xs"
                                                        size="sm"
                                                        color="grey"
                                                    >
                                                        <q-tooltip
                                                            anchor="bottom middle"
                                                            self="top middle"
                                                        >
                                                            {{ $t('Product') }}
                                                        </q-tooltip>
                                                    </q-icon>
                                                    {{ props.row.product_name }}
                                                </div>
                                                <div
                                                    v-if="
                                                        props.row
                                                            .rejected_production
                                                    "
                                                    class="q-mt-xs"
                                                >
                                                    <q-icon
                                                        name="far fa-xmark-to-slot"
                                                        class="q-mr-xs"
                                                        size="sm"
                                                        color="grey"
                                                    >
                                                        <q-tooltip
                                                            anchor="bottom middle"
                                                            self="top middle"
                                                        >
                                                            {{
                                                                $t(
                                                                    'Number of Rejected Products'
                                                                )
                                                            }}
                                                        </q-tooltip>
                                                    </q-icon>
                                                    {{
                                                        formatProductionValue(
                                                            props.row
                                                                .rejected_production
                                                        )
                                                    }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </q-card-section>
                        </q-card>
                    </div>
                </template>
            </q-table>
        </q-card-section>

        <!-- Dialog actions -->
        <q-card-section class="float-right q-pt-none">
            <q-btn
                class="q-mr-md"
                color="white"
                text-color="primary"
                @click="close"
                outline
                >{{ $t('Close') }}</q-btn
            >
        </q-card-section>
    </q-card>
</template>

<script>
import DefectCard from '@components/DefectCard.vue';
import MissingDefectLabel from '@components/MissingDefectLabel.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import { getLocaleFormat } from '@models/timeformats.js';
import { formatProductionValue } from '@utils/numberFormatters.js';
import { throttle } from 'quasar';
import { mapGetters } from 'vuex';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'QualityEventsTable',
    components: {
        MissingDefectLabel,
        DefectCard
    },
    mixins: [UpdateURL],
    emits: ['defectJustified', 'close'],
    props: {
        filters: {
            type: Object,
            required: true
        }
    },
    created() {
        // add throttling to the getDeviceStatus function to prevent users
        // from triggering too many requests
        this.throttledOnRequest = throttle(this.onRequest, 200);
    },
    mounted() {
        // load event list
        this.onRequest({
            pagination: this.table.pagination
        });
    },
    data() {
        return {
            // base url
            baseURL: 'quality_events/',

            // event table
            table: {
                data: [],
                columns: [
                    {
                        name: 'asset__name',
                        field: 'asset_name',
                        label: this.$t('Asset'),
                        required: true,
                        align: 'left',
                        sortable: true,
                        classes: 'events-table-cell'
                    },
                    {
                        name: 'status',
                        field: 'status',
                        label: this.$t('Status'),
                        required: true,
                        align: 'left',
                        sortable: false,
                        classes: 'events-table-cell'
                    },
                    {
                        name: 'start',
                        field: 'start',
                        label: this.$t('Start Time'),
                        align: 'left',
                        sortable: true,
                        format: val => this.formatDate(val)
                    },
                    {
                        name: 'end',
                        field: 'end',
                        label: this.$t('End Time'),
                        align: 'left',
                        sortable: true,
                        format: val => this.formatDate(val)
                    },
                    {
                        name: 'product__name',
                        field: 'product_name',
                        label: this.$t('Product'),
                        required: true,
                        align: 'left',
                        sortable: true,
                        classes: 'events-table-cell'
                    },
                    {
                        name: 'defect__name',
                        field: 'defect_name',
                        label: this.$t('Defect'),
                        align: 'left'
                    },
                    {
                        name: 'rejectedProduction',
                        field: 'rejected_production',
                        label: this.$t('Number of Rejected Products'),
                        align: 'left',
                        format: val => formatProductionValue(val)
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                isLoading: false
            },
            // selector for a defect
            defectSelectorProps: {
                url: `assets/${this.filters.assetId}/defects/`,
                queryParams: { ordering: 'name' },
                label: this.$t('Defect'),
                dialogTitle: this.$t('Select a Defect'),
                searchLabel: this.$t('Search a defect'),
                selection: null
            }
        };
    },
    computed: {
        ...mapGetters({
            getAsset: 'facilities/getAsset'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get URL with query parameters.
         * @return {String}
         */
        url() {
            // get current pagination state attributes
            const { descending, page, sortBy } = this.table.pagination;

            let queryParams = {
                start__lt: this.filters.end,
                end__gt: this.filters.start,
                ordering: descending ? `-${sortBy}` : `${sortBy}`,
                page: page
            };

            if (this.filters.defectId) {
                queryParams['defect'] = this.filters.defectId;
            } else {
                queryParams['has_defect'] = false;
            }

            // add product or asset ID if provided to queryparams
            if (this.filters.productId) {
                queryParams['product'] = this.filters.productId;
            } else if (this.filters.assetId) {
                queryParams['asset'] = this.filters.assetId;
            }

            return this.updateURL({
                baseURL: this.baseURL,
                params: queryParams
            });
        }
    },
    methods: {
        formatProductionValue,
        /**
         * Close dialog.
         */
        close() {
            this.$emit('close');
        },
        /**
         * Format date.
         * @param {String} datetime
         * @return {String} datetime in given locale.
         */
        formatDate(datetime) {
            return getLocaleFormat(datetime, this.userLocale);
        },
        /**
         * Handle requesting data from the server. Triggered by any table sort/search/page changes.
         * @param {Object} props
         */
        async onRequest(props) {
            // begin loading
            this.table.isLoading = true;

            // update pagination object
            this.table.pagination = props.pagination;

            // get events
            await this.getEvents();
        },
        /**
         * Fetch events from the server.
         */
        async getEvents() {
            await this.$axios
                .get(this.url)
                .then(response => {
                    // push data
                    this.table.data = response.data.results;
                    // update total events count
                    this.table.pagination.rowsNumber = response.data.count;
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to load events. Please try again later'
                        ),
                        error
                    );
                })
                .finally(() => {
                    this.table.isLoading = false;
                });
        },
        /**
         * Refetch the results from the server.
         */
        async refreshList() {
            // fetch data from the server
            await this.onRequest({
                pagination: this.table.pagination,
                filter: ''
            });
        },
        /**
         * Select/ deselect defect and products in table-select component
         * @param {Object} row - Event from table row.
         * @param {Object} props - Table selection props (productTableSelect, MissingDefectLabel)
         */
        select(row, props) {
            if (props.selection && props.selection.value === row.id) {
                props.selection = null;
            } else {
                props.selection = {
                    label: row.name,
                    value: row.id
                };
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.dialog__lg {
    width: 80%;
    max-width: none;
}

.dialog__xs {
    width: 100%;
    max-width: none;
}

.events-table-actions-col {
    width: 100px;
}

.events-table-button {
    padding: 6px;
    font-size: 12px;
}
</style>
